import { PromotionItem, usePromotion, usePromotionsList } from '@/lib/api/promotions'
import { useStore } from '@/lib/api/stores'
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '../ui/input'
import { Loader2, Search, Tag } from 'lucide-react'
import { cn } from "@/lib/utils"
import { getPromotionDiscount, formatDiscount } from '@/lib/promotion-discount'
import { useDropdownPosition } from '@/hooks/use-dropdown-position'

interface SearchPromotionInputProps {
    storeId: string
    excludeIds?: string[]
    onSelect: (promotion: PromotionItem) => void
}

/**
 * Filters promotions by excluding already selected ones
 */
const filterExcludedPromotions = (
    promotions: PromotionItem[],
    excludeIds: string[],
): PromotionItem[] => promotions.filter((p) => !excludeIds.includes(p.id))

/**
 * Search input component for finding and selecting promotions
 */
export const SearchPromotionInput = ({
    storeId,
    excludeIds = [],
    onSelect,
}: SearchPromotionInputProps) => {
    const { t } = useTranslation('common')
    const [searchValue, setSearchValue] = useState('')
    const [focused, setFocused] = useState(false)
    const showDropdown = focused
    const { containerRef, showAbove } = useDropdownPosition(showDropdown)

    const { data, isLoading } = usePromotionsList({
        storeId,
        search: searchValue || undefined,
        pagination: { page: 0, pageSize: 10 },
    })

    const filteredPromotions = useMemo(
        () => filterExcludedPromotions(data?.items ?? [], excludeIds),
        [data?.items, excludeIds],
    )

    const handleSelect = (promotion: PromotionItem) => {
        setSearchValue('')
        setFocused(false)
        onSelect(promotion)
    }

    const hasContent = isLoading || filteredPromotions.length > 0

    return (
        <div className="relative" ref={containerRef}>
            <div className="relative">
                <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
                <Input
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value ?? '')}
                    placeholder={t('formComponents.searchPromotion')}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setTimeout(() => setFocused(false), 200)}
                    className="pl-10"
                />
            </div>
            {showDropdown && hasContent && (
                <PromotionDropdown
                    isLoading={isLoading}
                    promotions={filteredPromotions}
                    onSelect={handleSelect}
                    showAbove={showAbove}
                    storeId={storeId}
                />
            )}
        </div>
    )
}

interface PromotionDropdownProps {
    isLoading: boolean
    promotions: PromotionItem[]
    onSelect: (promotion: PromotionItem) => void
    showAbove: boolean
    storeId: string
}

/**
 * Dropdown list showing search results
 */
const PromotionDropdown = ({
    isLoading,
    promotions,
    onSelect,
    showAbove,
    storeId,
}: PromotionDropdownProps) => (
    <div
        className={cn(
            'bg-background absolute z-50 max-h-[300px] w-full overflow-y-auto rounded-lg border shadow-lg',
            showAbove ? 'bottom-full mb-1' : 'top-full mt-1',
        )}
    >
        {isLoading && (
            <div className="flex items-center justify-center p-4">
                <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
            </div>
        )}
        {!isLoading &&
            promotions.map((promotion) => (
                <PromotionDropdownItem
                    key={promotion.id}
                    promotion={promotion}
                    onSelect={onSelect}
                    storeId={storeId}
                />
            ))}
    </div>
)

interface PromotionDropdownItemProps {
    promotion: PromotionItem
    onSelect: (promotion: PromotionItem) => void
    storeId: string
}

/**
 * Single promotion item in the dropdown — fetches details to surface the
 * discount value next to the (ellipsised) name.
 */
const PromotionDropdownItem = ({
    promotion,
    onSelect,
    storeId,
}: PromotionDropdownItemProps) => {
    const { data: details } = usePromotion(promotion.id, storeId)
    const { data: store } = useStore(storeId)
    const discountLabel = formatDiscount(getPromotionDiscount(details?.actions, store?.baseCurrency || 'USD'))

    return (
        <div
            onClick={() => onSelect(promotion)}
            className={cn(
                'flex cursor-pointer items-center gap-3 p-3 transition-colors',
                'hover:bg-accent border-b last:border-b-0',
            )}
        >
            <div className="bg-muted flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md">
                <Tag className="text-muted-foreground h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <p className="truncate font-medium">{promotion.name}</p>
                    {discountLabel && (
                        <span className="flex-shrink-0 rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                            {discountLabel}
                        </span>
                    )}
                </div>
                {promotion.code && (
                    <p className="text-muted-foreground truncate text-sm">
                        Code: {promotion.code}
                    </p>
                )}
            </div>
            <PromotionStatusBadge enabled={promotion.enabled} />
        </div>
    )
}

interface PromotionStatusBadgeProps {
    enabled: boolean | null
}

/**
 * Status badge showing if promotion is active
 */
const PromotionStatusBadge = ({ enabled }: PromotionStatusBadgeProps) => {
    const { t } = useTranslation('common')
    return (
        <span
            className={cn(
                'flex-shrink-0 rounded-full px-2 py-1 text-xs font-medium',
                enabled
                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
            )}
        >
            {enabled ? t('formComponents.active') : t('formComponents.inactive')}
        </span>
    )
}

